import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';

import { KeyValueType } from '@models/common';
import { GenericObjectType } from '@models/helpers';
import { CipoTableOptions, TableColumn, TableRow } from '@shared/components/data-list/common';
import { PagedDataListComponent } from '@shared/components/data-list/paged-data-list/paged-data-list.component';
import { DEBOUNCE_TIMES } from '@shared/consts';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoDialogModule } from '@shared/modules';

/** A placeholder entry can be a plain label (legacy dictionaries) or a rich object with its own key/value/description. */
type FroalaPlaceholderEntry = string | { key?: string; value?: string; description?: string };

export type FroalaPlaceholdersDialogData = {
  variables: GenericObjectType<string, FroalaPlaceholderEntry>;
  variableDescriptions?: GenericObjectType;
};

type PlaceholderRow = TableRow & KeyValueType & { description?: string };

/** Lists all `#` placeholders available in the froala editor (key + description) with search, for insertion at the cursor position. */
@Component({
  selector: 'app-froala-placeholders-dialog',
  imports: [
    CipoDialogModule,
    CipoDialogHeaderComponent,
    PagedDataListComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './placeholders-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaceholdersDialogComponent {
  private readonly data = inject<FroalaPlaceholdersDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<PlaceholdersDialogComponent, KeyValueType | undefined>>(MatDialogRef);

  readonly searchControl = new FormControl('');
  private readonly searchTerm = signal('');

  private readonly allRows = computed<PlaceholderRow[]>(() => {
    const descriptions = this.data.variableDescriptions ?? {};
    return Object.entries(this.data.variables ?? {}).map(([dictKey, entry]) => {
      if (typeof entry === 'object') {
        const key = entry.key ?? dictKey;
        return { key, value: entry.value ?? '', description: entry.description ?? descriptions[key] ?? '' };
      }
      return { key: dictKey, value: entry ?? '', description: descriptions[dictKey] ?? '' };
    });
  });

  readonly tableData = computed<PlaceholderRow[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.allRows();
    }
    return this.allRows().filter(
      ({ value, description }) => value?.toLowerCase().includes(term) || description?.toLowerCase().includes(term),
    );
  });

  readonly tableColumns: TableColumn[] = [
    { id: 1, name: 'value', displayName: 'FROALA.placeholders.valueColumn', width: 40, sortable: true },
    { id: 2, name: 'description', displayName: 'FROALA.placeholders.descriptionColumn', width: 60, sortable: true },
  ];

  readonly tableOptions: CipoTableOptions = {
    clickableRows: true,
  };

  constructor() {
    this.searchControl.valueChanges
      .pipe(debounceTime(DEBOUNCE_TIMES.short), takeUntilDestroyed())
      .subscribe(value => this.searchTerm.set(value ?? ''));
  }

  onRowClicked(row: TableRow) {
    this.dialogRef.close(row as PlaceholderRow);
  }
}
